import { Vector3 } from "three";

import type { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";
import type { CvJson } from "../../shared/types/cvJson";
import type { InsightsJson } from "../../shared/types/insightsEtcTypes";

export type ShotUniverseShotFamily =
  | "all"
  | "dinkDrop"
  | "driveSpeedup"
  | "lob";

export type ShotUniverseLabel = "safeNeutral" | "aggressive" | "bad" | "unknown";
const NET_HEIGHT_FT = 3;
const MIN_OPTION_NET_CLEARANCE_FT = 0.12;
const MAX_RAW_CV_ANCHOR_DISTANCE_FT = 8;

export type ShotUniverseOption = {
  id: string;
  shotFamily: Exclude<ShotUniverseShotFamily, "all">;
  target: Vector3;
  apexHeight: number;
  trajectory: Vector3[];
  netClearanceFt: number | null;
  heuristicScore: number;
  heuristicLabel: ShotUniverseLabel;
};

export type ShotUniversePlayerPosition = {
  playerId: number;
  teamId: 0 | 1;
  position: Vector3;
};

export type ShotUniverseMovementCue = {
  playerId: number;
  teamId: 0 | 1;
  from: Vector3;
  to: Vector3;
  shotNumber: number;
  nextShotNumber: number;
};

export type ShotUniverseContext = {
  rallyIndex: number;
  shotNumber: number;
  shotIndex: number;
  hitterPlayerId: number | null;
  contactPoint: Vector3 | null;
  actualTrajectory: Vector3[];
  playerPositions: ShotUniversePlayerPosition[];
  previousOpponentPositions: ShotUniversePlayerPosition[];
  nextOpponentPositions: ShotUniversePlayerPosition[];
  movementCues: ShotUniverseMovementCue[];
  options: ShotUniverseOption[];
  selectedShotType: string | null;
  selectedShot: ShotNumberOverlay | null;
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function teamOfPlayer(playerId: number | null | undefined): 0 | 1 {
  return playerId != null && playerId >= 2 ? 1 : 0;
}

export function playerColor(playerId: number | null | undefined): string {
  switch (playerId) {
    case 0:
      return "#4da3ff";
    case 1:
      return "#9bcfff";
    case 2:
      return "#52d27c";
    case 3:
      return "#a7efbd";
    default:
      return "#e8e8e8";
  }
}

export function pointToThree(point: { x: number; y: number; z?: number }): Vector3 {
  return new Vector3(point.x - 10, Math.max(0, point.z ?? 0), point.y - 22);
}

function maybePointToThree(point: any): Vector3 | null {
  if (!point) return null;
  const source = Array.isArray(point)
    ? { x: point[0], y: point[1], z: point[2] }
    : point.location && typeof point.location === "object"
      ? point.location
      : point;
  const x = Number(source.x);
  const y = Number(source.y);
  const z = Number(source.z ?? 0);
  if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(z)) return null;
  return pointToThree({ x, y, z });
}

function makeArc(start: Vector3, target: Vector3, apexHeight: number, steps = 20): Vector3[] {
  const out: Vector3[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const base = start.clone().lerp(target, t);
    const arcLift = 4 * t * (1 - t) * apexHeight;
    out.push(new Vector3(base.x, Math.max(base.y, 0) + arcLift, base.z));
  }
  return out;
}

function approximateNetClearance(points: Vector3[]): number | null {
  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1];
    const current = points[i];
    if ((previous.z <= 0 && current.z >= 0) || (previous.z >= 0 && current.z <= 0)) {
      const span = current.z - previous.z;
      const t = Math.abs(span) < 0.001 ? 0 : (0 - previous.z) / span;
      return previous.y + (current.y - previous.y) * t - NET_HEIGHT_FT;
    }
  }
  return null;
}

function actualTrajectoryFromShot(shot: ShotNumberOverlay | null, rawShot: any | null): Vector3[] {
  const rawTrajectory = rawShot?.resulting_ball_movement?.trajectory;
  const start =
    maybePointToThree(shot?.trajectoryStart?.location) ??
    maybePointToThree(rawTrajectory?.start?.location);
  const peak =
    maybePointToThree(shot?.trajectoryPeak) ??
    maybePointToThree(rawTrajectory?.peak);
  const end =
    maybePointToThree(shot?.trajectoryEnd?.location) ??
    maybePointToThree(rawTrajectory?.end?.location);
  if (!start || !end) return [];
  if (!peak) return makeArc(start, end, 2.5, 20);

  const out: Vector3[] = [];
  for (let i = 0; i <= 20; i += 1) {
    const t = i / 20;
    const a = start.clone().lerp(peak, t);
    const b = peak.clone().lerp(end, t);
    out.push(a.lerp(b, t));
  }
  return out;
}

function hasHorizontalTurnback(points: Vector3[]): boolean {
  if (points.length < 4) return false;
  const start = points[0];
  const end = points[points.length - 1];
  if (!start || !end) return false;

  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const directDistance = Math.hypot(dx, dz);
  if (directDistance < 8) return false;

  let maxProgress = 0;
  for (const point of points) {
    const progress = ((point.x - start.x) * dx + (point.z - start.z) * dz) / directDistance;
    if (maxProgress - progress > 2) return true;
    maxProgress = Math.max(maxProgress, progress);
  }
  return false;
}

function actualTrajectoryFromCv(args: {
  cvJson: CvJson | null | undefined;
  row: KitchenArrivalRow;
  shot: ShotNumberOverlay | null;
  rawShot: any | null;
  nextShot: ShotNumberOverlay | null;
  nextRawShot: any | null;
}): Vector3[] {
  const { cvJson, row, shot, rawShot, nextShot, nextRawShot } = args;
  const rally = cvJson?.sessions?.[0]?.rallies?.[row.rallyIndex];
  const frames = rally?.frames;
  const fps = Number(cvJson?.camera?.fps);
  if (!Array.isArray(frames) || !frames.length || !isFiniteNumber(fps) || fps <= 0) {
    return [];
  }

  const startMs = getShotMsRel(row, shot, rawShot);
  if (startMs == null) return [];

  const nextShotMs = nextShot || nextRawShot ? getShotMsRel(row, nextShot, nextRawShot) : null;
  const rawEndMs = shot?.trajectoryEnd?.ms ?? rawShot?.resulting_ball_movement?.trajectory?.end?.ms;
  const endTrajectoryMs = isFiniteNumber(rawEndMs)
    ? normalizeShotMsForVideo(row, rawEndMs)
    : null;
  const endMs =
    nextShotMs != null
      ? Math.max(startMs + 80, nextShotMs - 40)
      : endTrajectoryMs != null
        ? Math.max(startMs + 80, endTrajectoryMs + 120)
        : startMs + 2200;

  const startFrame = Math.max(0, Math.floor((startMs / 1000) * fps));
  const endFrame = Math.min(frames.length - 1, Math.ceil((endMs / 1000) * fps));
  if (endFrame <= startFrame) return [];

  const sampled: Vector3[] = [];
  const pushPoint = (courtPosition: any) => {
    if (
      !isFiniteNumber(courtPosition?.x) ||
      !isFiniteNumber(courtPosition?.y) ||
      !isFiniteNumber(courtPosition?.z)
    ) {
      return;
    }

    const next = pointToThree({
      x: courtPosition.x,
      y: courtPosition.y,
      z: courtPosition.z,
    });
    const previous = sampled[sampled.length - 1];
    if (previous && previous.distanceTo(next) < 0.15) return;
    if (previous && previous.distanceTo(next) > 14) return;
    sampled.push(next);
  };

  for (let frameIndex = startFrame; frameIndex <= endFrame; frameIndex += 1) {
    const frame = frames[frameIndex] as any;
    const event =
      frame?.balls?.ball ??
      frame?.balls?.shot ??
      frame?.balls?.bounce ??
      null;
    if (!event || event.out_of_sequence === true) continue;
    pushPoint(event.court_position);
  }

  if (sampled.length < 2) return [];

  const rawStart = maybePointToThree(
    shot?.trajectoryStart?.location ?? rawShot?.resulting_ball_movement?.trajectory?.start?.location,
  );
  const rawEnd = maybePointToThree(
    shot?.trajectoryEnd?.location ?? rawShot?.resulting_ball_movement?.trajectory?.end?.location,
  );
  const out: Vector3[] = [];
  const append = (point: Vector3 | null) => {
    if (!point) return;
    const previous = out[out.length - 1];
    if (previous && previous.distanceTo(point) < 0.15) return;
    if (previous && previous.distanceTo(point) > 14) return;
    out.push(point);
  };
  const firstSample = sampled[0];
  if (
    rawStart &&
    firstSample &&
    rawStart.distanceTo(firstSample) <= MAX_RAW_CV_ANCHOR_DISTANCE_FT
  ) {
    append(rawStart);
  }
  for (const point of sampled) {
    append(point);
  }
  const lastPoint = out[out.length - 1];
  if (
    rawEnd &&
    lastPoint &&
    rawEnd.distanceTo(lastPoint) <= MAX_RAW_CV_ANCHOR_DISTANCE_FT
  ) {
    append(rawEnd);
  }

  if (out.length < 3) return [];
  return hasHorizontalTurnback(out) ? [] : out;
}

function getOverlayBaseMs(row: KitchenArrivalRow): number {
  const firstAtMsRel = Number(row.shotNumberOverlays?.[0]?.atMsRel);
  return Number.isFinite(firstAtMsRel) ? firstAtMsRel : 0;
}

function normalizeShotMsForVideo(row: KitchenArrivalRow, rawMs: number): number | null {
  if (!Number.isFinite(rawMs)) return null;

  const segmentStartMs = Number(row.segmentStartMs ?? row.startMs ?? NaN);
  const segmentEndMs = Number(row.segmentEndMs ?? row.endMs ?? NaN);
  const segmentDurationMs =
    Number.isFinite(segmentStartMs) &&
    Number.isFinite(segmentEndMs) &&
    segmentEndMs > segmentStartMs
      ? segmentEndMs - segmentStartMs
      : null;
  const baseMs = getOverlayBaseMs(row);

  if (Number.isFinite(segmentStartMs)) {
    const fromSegmentStart = rawMs - segmentStartMs;
    if (
      fromSegmentStart >= -500 &&
      (segmentDurationMs == null || fromSegmentStart <= segmentDurationMs + 3000)
    ) {
      return Math.max(0, fromSegmentStart);
    }
  }

  if (Number.isFinite(baseMs) && baseMs > 0) {
    const fromOverlayBase = rawMs - baseMs;
    if (
      fromOverlayBase >= -500 &&
      (segmentDurationMs == null || fromOverlayBase <= segmentDurationMs + 3000)
    ) {
      return Math.max(0, fromOverlayBase);
    }
  }

  return rawMs;
}

function getShotMsRel(row: KitchenArrivalRow, shot: ShotNumberOverlay | null, rawShot: any | null) {
  if (isFiniteNumber(shot?.atMsRel)) {
    return normalizeShotMsForVideo(row, shot.atMsRel);
  }

  const rawStartMs = rawShot?.resulting_ball_movement?.trajectory?.start?.ms;
  if (isFiniteNumber(rawStartMs)) {
    return normalizeShotMsForVideo(row, rawStartMs);
  }

  return null;
}

function shotFromInsights(
  insightsJson: InsightsJson | null | undefined,
  rallyIndex: number,
  shotIndex: number,
): any | null {
  const rally = (insightsJson as any)?.rallies?.[rallyIndex];
  const shot = rally?.shots?.[shotIndex];
  return shot && typeof shot === "object" ? shot : null;
}

function getSelectedOverlay(
  row: KitchenArrivalRow | null | undefined,
  shotNumber: number,
): ShotNumberOverlay | null {
  return (
    row?.shotNumberOverlays?.find((overlay) => overlay.shotNumber === shotNumber) ??
    row?.shotNumberOverlays?.[Math.max(0, shotNumber - 1)] ??
    row?.shotNumberOverlays?.[0] ??
    null
  );
}

function getCvPlayerPositionsAtShot(args: {
  cvJson: CvJson | null | undefined;
  rallyIndex: number;
  shotMs: number | null;
  insightsToCvPlayerIndex?: Record<number, number>;
}): ShotUniversePlayerPosition[] {
  const { cvJson, rallyIndex, shotMs, insightsToCvPlayerIndex } = args;
  const rally = cvJson?.sessions?.[0]?.rallies?.[rallyIndex];
  const fps = Number(cvJson?.camera?.fps);
  if (!rally?.frames?.length || !isFiniteNumber(fps) || fps <= 0) return [];

  const targetFrame =
    shotMs != null && isFiniteNumber(shotMs)
      ? Math.max(0, Math.min(rally.frames.length - 1, Math.round((shotMs / 1000) * fps)))
      : 0;
  const frame = rally.frames[targetFrame] ?? rally.frames[0];

  const positions = frame?.player_court_positions ?? [];
  const playerIds = [0, 1, 2, 3];

  return playerIds
    .map((playerId): ShotUniversePlayerPosition | null => {
      const cvPlayerIndex =
        insightsToCvPlayerIndex?.[playerId] ??
        (insightsToCvPlayerIndex as any)?.[String(playerId)] ??
        playerId;
      const position = positions[cvPlayerIndex];
      if (!isFiniteNumber(position?.x) || !isFiniteNumber(position?.y)) return null;
      return {
        playerId,
        teamId: teamOfPlayer(playerId),
        position: pointToThree({ x: position.x, y: position.y, z: 0 }),
      };
    })
    .filter(
      (p: ShotUniversePlayerPosition | null): p is ShotUniversePlayerPosition => !!p,
    );
}

function getInsightPlayerPositions(shot: any): ShotUniversePlayerPosition[] {
  return (shot?.player_positions ?? [])
    .map((position: any, playerId: number): ShotUniversePlayerPosition | null => {
      if (!isFiniteNumber(position?.x) || !isFiniteNumber(position?.y)) return null;
      return {
        playerId,
        teamId: teamOfPlayer(playerId),
        position: pointToThree({ x: position.x, y: position.y, z: 0 }),
      };
    })
    .filter(
      (p: ShotUniversePlayerPosition | null): p is ShotUniversePlayerPosition => !!p,
    );
}

function mergeShotPlayerPositions(args: {
  insightPositions: ShotUniversePlayerPosition[];
  cvPositions: ShotUniversePlayerPosition[];
  hitterPlayerId: number | null;
  contactPoint: Vector3 | null;
}): ShotUniversePlayerPosition[] {
  const byPlayerId = new Map<number, ShotUniversePlayerPosition>();

  for (const position of args.cvPositions) {
    byPlayerId.set(position.playerId, position);
  }

  // Prefer insight positions when present; they are tied to the shot/contact event.
  for (const position of args.insightPositions) {
    byPlayerId.set(position.playerId, position);
  }

  if (
    args.hitterPlayerId != null &&
    !byPlayerId.has(args.hitterPlayerId) &&
    args.contactPoint
  ) {
    byPlayerId.set(args.hitterPlayerId, {
      playerId: args.hitterPlayerId,
      teamId: teamOfPlayer(args.hitterPlayerId),
      position: new Vector3(args.contactPoint.x, 0, args.contactPoint.z),
    });
  }

  return [0, 1, 2, 3]
    .map((playerId) => byPlayerId.get(playerId) ?? null)
    .filter(
      (position: ShotUniversePlayerPosition | null): position is ShotUniversePlayerPosition =>
        !!position,
    );
}

function inferFamilies(shotType: string | null): Array<Exclude<ShotUniverseShotFamily, "all">> {
  const s = (shotType ?? "").toLowerCase();
  if (s.includes("lob")) return ["lob"];
  if (s.includes("drive") || s.includes("speed") || s.includes("putaway")) {
    return ["driveSpeedup", "dinkDrop", "lob"];
  }
  if (s.includes("dink") || s.includes("drop") || s.includes("reset")) {
    return ["dinkDrop", "driveSpeedup", "lob"];
  }
  return ["dinkDrop", "driveSpeedup", "lob"];
}

function apexForFamily(family: Exclude<ShotUniverseShotFamily, "all">): number {
  if (family === "lob") return 13;
  if (family === "driveSpeedup") return 1.2;
  return 4.5;
}

function targetYRangeForContact(contactPoint: Vector3): [number, number] {
  // Three z is court length centered at the net. Positive z is Team 1 side.
  return contactPoint.z < 0 ? [2, 22] : [-22, -2];
}

function clampCourtTarget(point: Vector3, minZ: number, maxZ: number): Vector3 {
  return new Vector3(
    Math.max(-9.2, Math.min(9.2, point.x)),
    0.08,
    Math.max(Math.min(minZ, maxZ), Math.min(Math.max(minZ, maxZ), point.z)),
  );
}

function uniqueTargetPoints(points: Vector3[]): Vector3[] {
  const out: Vector3[] = [];
  for (const point of points) {
    if (
      out.some(
        (existing) =>
          Math.hypot(existing.x - point.x, existing.z - point.z) < 1.5,
      )
    ) {
      continue;
    }
    out.push(point);
  }
  return out;
}

function buildOpponentAwareTargets(args: {
  contactPoint: Vector3;
  opponents: ShotUniversePlayerPosition[];
  minZ: number;
  maxZ: number;
}): Vector3[] {
  const { contactPoint, minZ, maxZ } = args;
  const sign = contactPoint.z < 0 ? 1 : -1;
  const rawTargets: Vector3[] = [];

  const addTarget = (x: number, z: number) => {
    rawTargets.push(new Vector3(x, 0.08, z));
  };

  const lateralColumns = [-8.5, -4.25, 0, 4.25, 8.5];
  const depthRows = [2.5, 5.5, 10, 15, 19.5, 22].map(
    (distanceFromNet) => sign * distanceFromNet,
  );

  for (const z of depthRows) {
    for (const x of lateralColumns) {
      addTarget(x, z);
    }
  }

  return uniqueTargetPoints(
    rawTargets.map((point) => clampCourtTarget(point, minZ, maxZ)),
  );
}

function heuristicForTarget(args: {
  target: Vector3;
  family: Exclude<ShotUniverseShotFamily, "all">;
  opponents: ShotUniversePlayerPosition[];
}): { score: number; label: ShotUniverseLabel } {
  const { target, family, opponents } = args;
  const nearestOpponent = opponents.reduce((best, p) => {
    const d = Math.hypot(target.x - p.position.x, target.z - p.position.z);
    return Math.min(best, d);
  }, Number.POSITIVE_INFINITY);
  const inBounds = target.x >= -10 && target.x <= 10 && target.z >= -22 && target.z <= 22;
  const depthBonus = Math.abs(target.z) > 14 ? 1.2 : Math.abs(target.z) > 7 ? 0.6 : 0;
  const sidelineRisk = Math.abs(target.x) > 8.7 ? -1.3 : Math.abs(target.x) > 7.2 ? -0.45 : 0;
  const familyRisk = family === "driveSpeedup" ? -0.35 : family === "lob" ? -0.2 : 0.25;
  const openSpaceScore = Math.min(3, nearestOpponent / 4);
  const score = (inBounds ? 0 : -5) + depthBonus + sidelineRisk + familyRisk + openSpaceScore;

  if (!inBounds || score < 0.8) return { score, label: "bad" };
  if (score >= 2.5 || (family === "driveSpeedup" && nearestOpponent > 5.5)) {
    return { score, label: "aggressive" };
  }
  return { score, label: "safeNeutral" };
}

function buildOptions(args: {
  contactPoint: Vector3 | null;
  hitterPlayerId: number | null;
  shotType: string | null;
  playerPositions: ShotUniversePlayerPosition[];
}): ShotUniverseOption[] {
  const { contactPoint, hitterPlayerId, shotType, playerPositions } = args;
  if (!contactPoint) return [];
  const hitterTeam = teamOfPlayer(hitterPlayerId);
  const opponents = playerPositions.filter((p) => p.teamId !== hitterTeam);
  const families = inferFamilies(shotType);
  const [minZ, maxZ] = targetYRangeForContact(contactPoint);
  const targets = buildOpponentAwareTargets({
    contactPoint,
    opponents,
    minZ,
    maxZ,
  });

  const options: ShotUniverseOption[] = [];
  for (const family of families) {
    for (let targetIndex = 0; targetIndex < targets.length; targetIndex += 1) {
      const target = targets[targetIndex];
      const apexHeight = apexForFamily(family);
      const trajectory = makeArc(contactPoint, target, apexHeight, 18);
      const netClearanceFt = approximateNetClearance(trajectory);
      if (netClearanceFt != null && netClearanceFt <= MIN_OPTION_NET_CLEARANCE_FT) {
        continue;
      }
      const scored = heuristicForTarget({ target, family, opponents });
      options.push({
        id: `${family}-target-${targetIndex}`,
        shotFamily: family,
        target,
        apexHeight,
        trajectory,
        netClearanceFt,
        heuristicScore: scored.score,
        heuristicLabel: scored.label,
      });
    }
  }

  return options;
}

export function buildShotUniverseContext(args: {
  row: KitchenArrivalRow | null | undefined;
  rows: KitchenArrivalRow[];
  rallyIndex: number;
  shotNumber: number;
  insightsJson: InsightsJson | null | undefined;
  cvJson: CvJson | null | undefined;
  insightsToCvPlayerIndex?: Record<number, number>;
}): ShotUniverseContext | null {
  const row =
    args.row ??
    args.rows.find((candidate) => candidate.rallyIndex === args.rallyIndex) ??
    null;
  if (!row) return null;

  const shotNumber = Math.max(1, args.shotNumber || 1);
  const shotIndex = shotNumber - 1;
  const selectedShot = getSelectedOverlay(row, shotNumber);
  const previousShotNumber = shotNumber > 1 ? shotNumber - 1 : null;
  const nextShotNumber = shotNumber + 1 <= (row.shotNumberOverlays?.length ?? shotNumber)
    ? shotNumber + 1
    : null;
  const previousSelectedShot =
    previousShotNumber != null ? getSelectedOverlay(row, previousShotNumber) : null;
  const nextSelectedShot =
    nextShotNumber != null ? getSelectedOverlay(row, nextShotNumber) : null;
  const rawShot =
    selectedShot?.rawShot ??
    shotFromInsights(args.insightsJson, row.rallyIndex, shotIndex) ??
    null;
  const previousRawShot =
    previousSelectedShot?.rawShot ??
    (previousShotNumber != null
      ? shotFromInsights(args.insightsJson, row.rallyIndex, previousShotNumber - 1)
      : null) ??
    null;
  const nextRawShot =
    nextSelectedShot?.rawShot ??
    (nextShotNumber != null
      ? shotFromInsights(args.insightsJson, row.rallyIndex, nextShotNumber - 1)
      : null) ??
    null;
  const contactPoint =
    maybePointToThree(selectedShot?.trajectoryStart?.location) ??
    maybePointToThree(rawShot?.resulting_ball_movement?.trajectory?.start?.location);
  const nextContactPoint =
    maybePointToThree(nextSelectedShot?.trajectoryStart?.location) ??
    maybePointToThree(nextRawShot?.resulting_ball_movement?.trajectory?.start?.location);
  const selectedShotType = selectedShot?.shotType ?? rawShot?.shot_type ?? null;
  const hitterPlayerId =
    typeof selectedShot?.playerId === "number"
      ? selectedShot.playerId
      : typeof rawShot?.player_id === "number"
        ? rawShot.player_id
        : null;
  const nextHitterPlayerId =
    typeof nextSelectedShot?.playerId === "number"
      ? nextSelectedShot.playerId
      : typeof nextRawShot?.player_id === "number"
        ? nextRawShot.player_id
        : null;
  const shotMs = getShotMsRel(row, selectedShot, rawShot);
  const playerPositions =
    getCvPlayerPositionsAtShot({
      cvJson: args.cvJson,
      rallyIndex: row.rallyIndex,
      shotMs,
      insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
    }) ??
    [];
  const fallbackPositions = mergeShotPlayerPositions({
    insightPositions: getInsightPlayerPositions(rawShot),
    cvPositions: playerPositions,
    hitterPlayerId,
    contactPoint,
  });

  const cvActualTrajectory = actualTrajectoryFromCv({
    cvJson: args.cvJson,
    row,
    shot: selectedShot,
    rawShot,
    nextShot: nextSelectedShot,
    nextRawShot,
  });
  const actualTrajectory =
    cvActualTrajectory.length >= 3
      ? cvActualTrajectory
      : actualTrajectoryFromShot(selectedShot, rawShot);
  const hitterTeam = teamOfPlayer(hitterPlayerId);
  const previousShotMs = previousShotNumber
    ? getShotMsRel(row, previousSelectedShot, previousRawShot)
    : null;
  const previousCvPositions =
    previousShotNumber != null
      ? getCvPlayerPositionsAtShot({
          cvJson: args.cvJson,
          rallyIndex: row.rallyIndex,
          shotMs: previousShotMs,
          insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
        })
      : [];
  const previousContactPoint =
    maybePointToThree(previousSelectedShot?.trajectoryStart?.location) ??
    maybePointToThree(previousRawShot?.resulting_ball_movement?.trajectory?.start?.location);
  const previousHitterPlayerId =
    typeof previousSelectedShot?.playerId === "number"
      ? previousSelectedShot.playerId
      : typeof previousRawShot?.player_id === "number"
        ? previousRawShot.player_id
        : null;
  const previousFallbackPositions = mergeShotPlayerPositions({
    insightPositions: getInsightPlayerPositions(previousRawShot),
    cvPositions: previousCvPositions,
    hitterPlayerId: previousHitterPlayerId,
    contactPoint: previousContactPoint,
  });
  const previousOpponentPositions = previousFallbackPositions.filter(
    (position) => position.teamId !== hitterTeam,
  );
  const nextShotMs =
    nextShotNumber != null ? getShotMsRel(row, nextSelectedShot, nextRawShot) : null;
  const nextCvPositions =
    nextShotNumber != null
      ? getCvPlayerPositionsAtShot({
          cvJson: args.cvJson,
          rallyIndex: row.rallyIndex,
          shotMs: nextShotMs,
          insightsToCvPlayerIndex: args.insightsToCvPlayerIndex,
        })
      : [];
  const nextFallbackPositions = mergeShotPlayerPositions({
    insightPositions: getInsightPlayerPositions(nextRawShot),
    cvPositions: nextCvPositions,
    hitterPlayerId: nextHitterPlayerId,
    contactPoint: nextContactPoint,
  });
  const nextOpponentPositions = nextFallbackPositions.filter(
    (position) => position.teamId !== hitterTeam,
  );
  const currentPositionByPlayerId = new Map(
    fallbackPositions.map((position) => [position.playerId, position]),
  );
  const nextPositionByPlayerId = new Map(
    nextFallbackPositions.map((position) => [position.playerId, position]),
  );
  const movementCues: ShotUniverseMovementCue[] =
    nextShotNumber != null
      ? fallbackPositions
          .map((nextPosition): ShotUniverseMovementCue | null => {
            const currentPosition = currentPositionByPlayerId.get(nextPosition.playerId) ?? null;
            const destinationPosition = nextPositionByPlayerId.get(nextPosition.playerId) ?? null;
            if (!currentPosition) return null;
            if (!destinationPosition) return null;
            return {
              playerId: currentPosition.playerId,
              teamId: currentPosition.teamId,
              from: currentPosition.position,
              to: destinationPosition.position,
              shotNumber,
              nextShotNumber,
            };
          })
          .filter((cue): cue is ShotUniverseMovementCue => !!cue)
      : [];
  const options = buildOptions({
    contactPoint,
    hitterPlayerId,
    shotType: selectedShotType,
    playerPositions: fallbackPositions,
  });

  return {
    rallyIndex: row.rallyIndex,
    shotNumber,
    shotIndex,
    hitterPlayerId,
    contactPoint,
    actualTrajectory,
    playerPositions: fallbackPositions,
    previousOpponentPositions,
    nextOpponentPositions,
    movementCues,
    options,
    selectedShotType,
    selectedShot,
  };
}
